/** @typedef {import("../../api/types").Me} Me */
/** @typedef {import("../../api/types").OrdersPage} OrdersPage */
/** @typedef {import("../../api/types").Stats} Stats */
/** @typedef {import("../../api/types").Attention} Attention */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import {Button, Container} from "reactstrap";
import AttentionCard from "./components/AttentionCard";
import OrdersTable from "../orders/components/OrdersTable";
import {formatMoney} from "../../shared/utils/format";
import "./home.css";
import IconLabel from "../../shared/components/IconLabel";
import {ListIcon, PlusIcon} from "../../shared/components/icons";

const API = "/os/api/";

const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
};

/**
 * The front page: a greeting, the way to a new order, what is waiting on the
 * provider and the latest orders. The figures of the business are on the
 * Contabilidade page, and a line here says how the month is going and leads
 * there.
 */
const Home = () => {
    const [me, setMe] = useState(/** @type {Me|null} */ (null));
    const [stats, setStats] = useState(/** @type {Stats|null} */ (null));
    const [attention, setAttention] = useState(/** @type {Attention|null} */ (null));
    const [attentionFailed, setAttentionFailed] = useState(false);
    const [recent, setRecent] = useState(/** @type {OrdersPage["results"]|null} */ (null));

    useEffect(() => {
        const controller = new AbortController();
        const {signal} = controller;
        // Everything here is decorative: failures just leave blanks.
        const ignore = () => {};

        axios
            .get(`${API}me/`, {signal})
            .then(({data}) => setMe(data))
            .catch(ignore);

        axios
            .get(`${API}stats/`, {signal})
            .then(({data}) => setStats(data))
            .catch(ignore);

        axios
            .get(`${API}attention/`, {signal})
            .then(({data}) => setAttention(data))
            .catch((err) => {
                if (!axios.isCancel(err)) setAttentionFailed(true);
            });

        axios
            .get(`${API}service_orders/`, {params: {page_size: 5}, signal})
            .then(({data}) => setRecent(data.results))
            .catch(() => setRecent([]));

        return () => controller.abort();
    }, []);

    const name = me?.username?.trim().split(" ")[0] || me?.business_name;

    return (
        <Container className="mt-4 mb-5">
            <div className="home-hero mb-4">
                <h2 className="mb-1">
                    {greeting()}
                    {name ? `, ${name}` : ""}
                </h2>
                <p className="mb-3">
                    {me?.business_name ? `${me.business_name} — ` : ""}veja como estão as coisas hoje.
                </p>
                <div className="d-flex flex-wrap gap-2">
                    <Button color="light" tag={Link} to="/orders/new">
                        <IconLabel icon={<PlusIcon />}>Nova ordem</IconLabel>
                    </Button>
                    <Button color="light" outline tag={Link} to="/orders">
                        <IconLabel icon={<ListIcon />}>Ver ordens de serviço</IconLabel>
                    </Button>
                </div>
            </div>

            <div className="mb-3">
                <AttentionCard data={attention} error={attentionFailed} />
            </div>

            {stats && (
                <p className="home-month">
                    Neste mês: <strong>{formatMoney(stats.revenue_this_month, stats.currency)}</strong> recebidos, com
                    lucro de <strong>{formatMoney(stats.profit_this_month, stats.currency)}</strong>.{" "}
                    <Link to="/accounting">Ver a contabilidade</Link>
                </p>
            )}

            <div className="d-flex justify-content-between align-items-baseline mb-2">
                <h5 className="mb-0">Ordens recentes</h5>
                <Link to="/orders">Ver todas</Link>
            </div>
            <div className="home-recent">
                <OrdersTable
                    orders={recent}
                    emptyMessage="Nenhuma ordem de serviço ainda."
                    className="mb-0 align-middle"
                />
            </div>
        </Container>
    );
};

export default Home;
